





































































import { AxiosError } from 'axios';
import { defineComponent, ref, computed, watch } from '@vue/composition-api';
import {
  MandatoryItem,
  IMandatoryItemData,
  ISystemError,
  IMandatoryItem
} from '@/models';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';

import {
  useCreateTariffMandatoryItem,
  useUpdateTariffMandatoryItem
} from '@/composable/tariff-mandatory-item';
import { requiredValidator } from '@/utils/validators';
import { ALForm } from '@/common/component.types';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import CSIItemSearch from '@/components/csi/ItemSearch.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
export default defineComponent({
  name: 'TariffMandatoryItemFormDialog',
  components: {
    ALDialog,
    CSIItemSearch,
    ErrorAlert
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    tariffMandatoryItem: {
      type: Object as () => IMandatoryItem | null,
      default: null
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.value,
      set: value => {
        emit('input', value);
      }
    });

    const mandatoryItemData = ref<IMandatoryItemData>({
      item: ''
    });
    const isFormValid = ref(false);

    const formRef = ref<ALForm | null>(null);

    const {
      mutate: createTariffMandatoryItem,
      isLoading: isCreating,
      error: createError,
      reset: createReset
    } = useCreateTariffMandatoryItem();
    const {
      mutate: updateTariffMandatoryItem,
      isLoading: isUpdating,
      error: updateError,
      reset: updateReset
    } = useUpdateTariffMandatoryItem();

    const isLoading = computed(() => isCreating.value || isUpdating.value);
    const localError = ref<ISystemError | null>(null);

    const subtitle = computed(() => {
      return props.tariffMandatoryItem
        ? 'Edit Mandatory Item'
        : 'Create New Mandatory Item';
    });

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.tariffMandatoryItem
          ? updateError.value
          : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    watch(
      () => props.value,
      () => {
        resetFormValidation();
      }
    );
    watch(
      () => props.tariffMandatoryItem,
      newVal => {
        if (newVal) {
          mandatoryItemData.value = {
            id: newVal.id,
            item: newVal.item
          };
        } else {
          mandatoryItemData.value = {
            item: ''
          };
        }
      },
      { immediate: true }
    );

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    const closeDialog = () => {
      clearErrors();
      localDialog.value = false;
    };

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    const saveTariffMandatoryItem = async () => {
      const valid = await validateForm();

      if (valid) {
        let mandatoryItemInstance: IMandatoryItem | null = null;

        try {
          mandatoryItemInstance = new MandatoryItem(mandatoryItemData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (mandatoryItemInstance != null) {
          const saveAction = props.isEdit
            ? updateTariffMandatoryItem
            : createTariffMandatoryItem;

          let payload = mandatoryItemInstance;
          if (props.isEdit) {
            payload = diffDeep(
              mandatoryItemInstance,
              props.tariffMandatoryItem,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              emit('save');
              closeDialog();
            }
          });
        }
      }
    };

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    return {
      localDialog,
      isFormValid,
      formRef,
      mandatoryItemData,
      isLoading,
      error,
      subtitle,
      requiredValidator,
      clearErrors,
      closeDialog,
      saveTariffMandatoryItem
    };
  }
});
